import axios from 'axios'

/**
 * Normalized error shape consumed by the UI layer (toasts, inline form errors,
 * Sentry capture). Every API call funnels through `normalizeError` so callers
 * never have to introspect raw `AxiosError` objects.
 *
 * Source: architecture.md §3.6.
 */
export type AppError = {
  kind:
    | 'network'
    | 'auth'
    | 'forbidden'
    | 'validation'
    | 'not_found'
    | 'rate_limited'
    | 'server'
    | 'unknown'
  status?: number
  message: string
  detail?: unknown
  fieldErrors?: Record<string, string>
}

/** Loose shape of error payloads returned by the Go backend. */
type ServerErrorBody = {
  detail?: string
  message?: string
  errors?: Record<string, string>
}

/**
 * Convert any thrown value (typically an `AxiosError`) into an `AppError`.
 *
 * Behavior matrix (architecture §3.6):
 * - No `response` (network down / CORS / abort)        → kind='network'
 * - HTTP 401                                            → kind='auth'
 * - HTTP 403                                            → kind='forbidden'
 * - HTTP 404                                            → kind='not_found'
 * - HTTP 400 / 422 (Go validation envelope)             → kind='validation' + fieldErrors
 * - HTTP 5xx                                            → kind='server'
 * - Any other status (or non-axios value)               → kind='unknown'
 */
export function normalizeError(err: unknown): AppError {
  // The apiClient interceptor already rejects with an AppError; normalizing it
  // again used to turn it into "[object Object]".
  if (isAppError(err)) return err
  if (axios.isAxiosError(err)) {
    if (!err.response) {
      return { kind: 'network', message: 'Connection lost' }
    }
    const status = err.response.status
    const data = err.response.data as ServerErrorBody | undefined
    const detailMsg = data?.detail ?? data?.message

    if (status === 401) {
      return { kind: 'auth', status, message: detailMsg ?? 'Unauthorized' }
    }
    if (status === 403) {
      return {
        kind: 'forbidden',
        status,
        message: detailMsg ?? 'No permission',
      }
    }
    if (status === 404) {
      return { kind: 'not_found', status, message: detailMsg ?? 'Not found' }
    }
    if (status === 422 || status === 400) {
      return {
        kind: 'validation',
        status,
        message: detailMsg ?? 'Validation error',
        fieldErrors: data?.errors,
        detail: data,
      }
    }
    if (status === 429) {
      return {
        kind: 'rate_limited',
        status,
        message: detailMsg ?? 'Too many requests',
      }
    }
    if (status >= 500) {
      return { kind: 'server', status, message: detailMsg ?? 'Server error' }
    }
    // Carry the raw response body on the catch-all branch so structured
    // 409 responses (e.g. {"error": "pin_limit_reached", "limit": N})
    // remain readable to the caller — without `detail` the limit value
    // would be lost between axios and the React-Query mutation handler.
    return {
      kind: 'unknown',
      status,
      message: detailMsg ?? 'Request failed',
      detail: data,
    }
  }
  if (err instanceof Error) {
    return { kind: 'unknown', message: err.message }
  }
  return { kind: 'unknown', message: String(err) }
}

function isAppError(err: unknown): err is AppError {
  return (
    typeof err === 'object' &&
    err !== null &&
    !(err instanceof Error) &&
    typeof (err as AppError).kind === 'string' &&
    typeof (err as AppError).message === 'string'
  )
}

export const isAuthError = (e: AppError): boolean => e.kind === 'auth'
export const isValidationError = (e: AppError): boolean =>
  e.kind === 'validation'
export const isNetworkError = (e: AppError): boolean => e.kind === 'network'

/**
 * Human-facing reason for a failed request, for use as a toast `description`
 * under a generic title (e.g. "Не вдалося запустити" / "Не вдалося
 * імпортувати"). Rewrites the backend's machine-readable limit envelope into
 * something a non-engineer can act on; otherwise returns the server-provided
 * detail verbatim. Returns `undefined` when there's nothing more useful than
 * the title itself, so callers can pass it straight through without guarding.
 */
export function describeReason(e: AppError): string | undefined {
  const raw = e.message?.trim()
  if (!raw) return undefined
  // Backend limit envelope: "limit exceeded: <name> (current: N, max: M)"
  // (internal/core/errors.go LimitExceeded).
  const lim = /limit exceeded:\s*([a-z_]+)\s*\(current:\s*(\d+),\s*max:\s*(\d+)\)/i.exec(
    raw,
  )
  if (lim) {
    const [, name = '', current, max] = lim
    const label: Record<string, string> = {
      tasks_per_user: 'Вичерпано ліміт задач на акаунт',
      pending_tasks_per_user: 'Забагато задач у черзі одночасно',
      voice_clones_per_user: 'Вичерпано ліміт клонів голосу',
      monthly_clones_per_user: 'Вичерпано місячний ліміт клонів голосу',
    }
    return `${label[name] ?? `Вичерпано ліміт (${name})`}: ${current} з ${max}`
  }
  // Generic fallbacks injected by normalizeError carry no signal beyond the
  // title — don't clutter the toast with them.
  const GENERIC = new Set([
    'Request failed',
    'Server error',
    'Unauthorized',
    'No permission',
    'Not found',
    'Connection lost',
    'Validation error',
    'Too many requests',
  ])
  return GENERIC.has(raw) ? undefined : raw
}
